// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { AllowList } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Rbac/AllowList';
import FormDialog from '../../../components/Dialog/FormDialog';
import Loading from '../../../components/Loading';
import { useParticipantAddWalletsToAllowList } from '../../../hooks/mutations/registry/allowList/useParticipantAddWalletsToAllowList';
import { useRegistrarAddWalletsToAllowList } from '../../../hooks/mutations/registry/allowList/useRegistrarAddWalletsToAllowList';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import { AllowListViewMode } from '../../../hooks/queries/registry/allowList/useAllowListRole';
import TemplateContract from '../../../utils/TemplateContract';
import { ERR_ADD_WALLET, ERR_ADD_WALLET_NOT_FOUND, SUC_ADD_WALLET } from '../../../utils/strings';
import { groupCredentialsByHolder, isMemberOfAllowList } from './utils';

interface AddWalletDialogProps {
  open: boolean;
  onClose: () => void;
  allowList: TemplateContract<AllowList>;
  mode: AllowListViewMode;
  disableDialog?: boolean;
}

export const AddWalletDialog: React.FC<AddWalletDialogProps> = ({
  open,
  onClose,
  allowList,
  mode,
  disableDialog,
}) => {
  const [partyInput, setPartyInput] = useState('');
  const credentials = useCredentials();
  const registrarAddWallets = useRegistrarAddWalletsToAllowList();
  const participantAddWallets = useParticipantAddWalletsToAllowList();
  const addWallets = mode === 'registrar' ? registrarAddWallets : participantAddWallets;
  const { enqueueSnackbar } = useSnackbar();
  const trimmedParty = partyInput.trim();
  const isFormValid = trimmedParty !== '';

  // Group credentials by holder and filter out holders who are already members
  const availableHolders = useMemo(() => {
    if (!credentials.data) return [];

    // Find holders who are already members
    const existingMemberHolders = new Set<string>();
    credentials.data.forEach((cred) => {
      if (isMemberOfAllowList(cred, allowList.payload.name)) {
        existingMemberHolders.add(cred.payload.holder);
      }
    });

    // Filter out credentials from existing member holders, then group by holder
    const availableCredentials = credentials.data.filter(
      (cred) => !existingMemberHolders.has(cred.payload.holder),
    );

    return groupCredentialsByHolder(availableCredentials);
  }, [credentials.data, allowList.payload.name]);

  const handleSubmit = async () => {
    if (!isFormValid) return;

    // Find the holder matching the entered party
    const matchingHolder = availableHolders.find((h) => h.holder === trimmedParty);

    if (!matchingHolder) {
      enqueueSnackbar(ERR_ADD_WALLET_NOT_FOUND, {
        variant: 'error',
      });
      return;
    }

    // Get all credential IDs for the matching holder
    const credentialCids = matchingHolder.credentials.map((c) => c.contractId);

    try {
      await addWallets.mutateAsync({
        allowListCid: allowList.contractId,
        credentialCids,
      });
      enqueueSnackbar(SUC_ADD_WALLET, { variant: 'success' });
      setPartyInput('');
      onClose();
    } catch (submitError) {
      console.error(ERR_ADD_WALLET, submitError);
      enqueueSnackbar(ERR_ADD_WALLET, { variant: 'error' });
    }
  };

  const handleCancel = () => {
    setPartyInput('');
    onClose();
  };

  const isLoading = credentials.isLoading || !credentials.data || addWallets.isPending;

  return (
    <FormDialog
      open={open}
      title="Add Wallet to Allow List"
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitName="Add Wallet"
      isSubmitDisabled={!isFormValid || isLoading}
      disableDialog={disableDialog}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <Box>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Enter the ID of the party you wish to add to this allow list.
          </Typography>
          <TextField
            label="Party"
            value={partyInput}
            onChange={(e) => {
              setPartyInput(e.target.value);
            }}
            fullWidth
            placeholder="Enter party ID"
            InputLabelProps={{ shrink: true }}
            InputProps={{ notched: true }}
          />
        </Box>
      )}
    </FormDialog>
  );
};
